import { Snackbar, Alert, InputLabel, MenuItem, Select } from '@mui/material';
import Box from '@mui/material/Box';
import { Formik } from 'formik';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { forgotPassword } from 'src/api/csApi';
import { LoginButton, LoginInput } from 'src/components/mui-overrides/Login';
import { ErrorMessage } from 'src/components/ui/ErrorMessage';
import { Colors } from 'src/components/utils/Theme';
import { Routes } from 'src/router/Routes';
import { authStore } from 'src/stores/AuthStore';
import { generalStore } from 'src/stores/GeneralStore';
import { object, string } from 'yup';

export const AuthForgotPasswordSite: FC = () => {
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const { t } = useTranslation();
  const validationSchema = object({
    email: string().email(t('forgotpassword.email.validation.invalid')).required(t('forgotpassword.email.validation.required')).trim(),
  });
  const [showError, setShowError] = useState(false);

  const initialValues = {
    email: '',
    platformProvider: 'brickwise',
  };

  const handleSubmit = async (values: { email: string; platformProvider: string }, actions: any) => {
    try {
      setShowError(false);
      generalStore.setIsLoading(true);
      await forgotPassword(values.email, values.platformProvider);
      if (authStore.error) {
        setShowError(true);
      } else {
        actions.resetForm();
        setShowSuccessAlert(true);
      }
    } catch (error) {
      generalStore.setError('Unknown error');
    }

    generalStore.setIsLoading(false);
  };

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={showSuccessAlert}
        autoHideDuration={6000}
        onClose={() => setShowSuccessAlert(false)}
      >
        <Alert onClose={() => setShowSuccessAlert(false)} severity="success" sx={{ width: '100%' }}>
          {t('forgotpassword.success')}
        </Alert>
      </Snackbar>
      <div
        style={{
          flexGrow: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: showError ? '5px solid ' + Colors.SECONDARY_COLOR : '',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            flexGlow: 1,
            maxWidth: 350,
          }}
        >
          <img style={{ width: 170, marginBottom: 25 }} src="/logo.svg" alt="brickwise-logo" />

          <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
            {(formik) => (
              <form onSubmit={formik.handleSubmit}>
                <LoginInput
                  margin="normal"
                  fullWidth
                  id="email"
                  label={t('forgotpassword.email.label')}
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  variant="filled"
                  helperText={formik.errors.email && formik.touched.email ? formik.errors.email : ''}
                />
                <InputLabel id="demo-simple-select-label">Platform Provider</InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={formik.values.platformProvider}
                  label="platform"
                  style={{ width: '100%', marginBottom: 16 }}
                  onChange={(value) => {
                    // manually changing the value
                    formik.setFieldValue('platformProvider', value.target.value);
                  }}
                >
                  <MenuItem value={'brickwise'}>Brickwise</MenuItem>
                  <MenuItem value={'realpart'}>RealPart</MenuItem>
                  <MenuItem value={'coppen'}>Coppen</MenuItem>
                  <MenuItem value={'prokon'}>Prokon</MenuItem>
                </Select>

                <LoginButton size="large" type="submit" fullWidth variant="contained" style={{ marginTop: 16 }}>
                  {t('forgotpassword.button.label')}
                </LoginButton>
                <div style={{ paddingTop: 24, textAlign: 'center' }}>
                  <Link style={{ color: 'black' }} to={Routes.ROOT}>
                    {t('forgotpassword.login')}
                  </Link>
                </div>
              </form>
            )}
          </Formik>
          <ErrorMessage
            style={{
              visibility: showError ? 'visible' : 'hidden',
            }}
            message={'Unknown error'}
          />
        </Box>
      </div>
    </>
  );
};
